/**
 * V5.3: 공휴일 목록 테이블
 *
 * 기능:
 * - 정렬 가능한 컬럼 (날짜, 요일, 이름, 유형, 상태, 수정일)
 * - 기본 정렬: 날짜 오름차순
 * - national 타입 보호 정책 시각화 (잠금 아이콘)
 * - 수정/삭제 액션
 *
 * V5.3 변경:
 * - Tailwind 클래스 제거, CSS 모듈로 전환
 * - overflow-x: auto로 가로 스크롤 지원
 */

import React, { useMemo } from 'react';
import { formatDateTimeByCompanyCode } from '../../../../shared/utils/dateUtils';  // V62
import {
  Edit2,
  Trash2,
  ChevronUp,
  ChevronDown,
  Lock,
  ChevronsUpDown,
  Calendar,
} from 'lucide-react';
import {
  Holiday,
  HolidayType,
  SortConfig,
  SortField,
  HOLIDAY_TYPE_LABELS,
  HOLIDAY_SOURCE_LABELS,
  getDayOfWeekLabel,
  getDayOfWeekIndex,
  formatTimestamp,
} from './types';
import styles from './holidaysStyles.module.css';

interface HolidayTableProps {
  holidays: Holiday[];
  isLoading: boolean;
  sortConfig: SortConfig;
  onSortChange: (field: SortField) => void;
  onEdit?: (holiday: Holiday) => void;   // V60: optional for read-only
  onDelete?: (holiday: Holiday) => void; // V60: optional for read-only
}

export function HolidayTable({
  holidays,
  isLoading,
  sortConfig,
  onSortChange,
  onEdit,
  onDelete,
}: HolidayTableProps) {
  // 정렬된 공휴일 목록
  const sortedHolidays = useMemo(() => {
    return [...holidays].sort((a, b) => {
      const { field, direction } = sortConfig;
      const modifier = direction === 'asc' ? 1 : -1;

      switch (field) {
        case 'date':
          return a.date.localeCompare(b.date) * modifier;
        case 'name':
          return a.name.localeCompare(b.name) * modifier;
        case 'type':
          return (HOLIDAY_TYPE_LABELS[a.type] || '').localeCompare(
            HOLIDAY_TYPE_LABELS[b.type] || ''
          ) * modifier;
        default:
          return 0;
      }
    });
  }, [holidays, sortConfig]);

  // 정렬 헤더 렌더링
  const SortableHeader = ({
    field,
    label,
    width,
  }: {
    field: SortField;
    label: string;
    width?: string;
  }) => {
    const isActive = sortConfig.field === field;

    return (
      <th
        className={styles.tableHeaderCellSortable}
        style={{ width }}
        onClick={() => onSortChange(field)}
      >
        <div className={`${styles.sortIndicator} ${isActive ? styles.sortActive : styles.sortInactive}`}>
          {label}
          {isActive ? (
            sortConfig.direction === 'asc' ? (
              <ChevronUp />
            ) : (
              <ChevronDown />
            )
          ) : (
            <ChevronsUpDown />
          )}
        </div>
      </th>
    );
  };

  // 유형 배지 클래스
  const getTypeBadgeClass = (type: HolidayType): string => {
    switch (type) {
      case 'national':
        return styles.typeBadgeNational;
      case 'company':
        return styles.typeBadgeCompany;
      case 'temporary':
        return styles.typeBadgeTemporary;
      default:
        return styles.typeBadgeTemporary;
    }
  };

  // 요일 클래스
  const getDayClass = (dayIndex: number): string => {
    if (dayIndex === 0) return styles.daySunday;
    if (dayIndex === 6) return styles.daySaturday;
    return styles.dayWeekday;
  };

  // national 타입 여부
  const isNational = (holiday: Holiday): boolean => holiday.type === 'national';

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead className={styles.tableHeader}>
          <tr>
            <SortableHeader field="date" label="날짜" width="120px" />
            <th className={styles.tableHeaderCellCenter} style={{ width: '72px' }}>
              요일
            </th>
            <SortableHeader field="name" label="공휴일명" width="180px" />
            <SortableHeader field="type" label="유형" width="100px" />
            <th className={styles.tableHeaderCellCenter} style={{ width: '80px' }}>
              소스
            </th>
            <th className={styles.tableHeaderCellCenter} style={{ width: '160px' }}>
              수정일
            </th>
            {(onEdit || onDelete) && (
              <th className={styles.tableHeaderCellCenter} style={{ width: '100px' }}>
                작업
              </th>
            )}
          </tr>
        </thead>
        <tbody className={styles.tableBody}>
          {isLoading ? (
            <tr>
              <td colSpan={(onEdit || onDelete) ? 7 : 6}>
                <div className={styles.loadingState}>
                  <div className={styles.loadingStateContent}>
                    <div className={styles.loadingSpinner} />
                    <span className={styles.loadingText}>공휴일 목록을 불러오는 중...</span>
                  </div>
                </div>
              </td>
            </tr>
          ) : sortedHolidays.length === 0 ? (
            <tr>
              <td colSpan={(onEdit || onDelete) ? 7 : 6}>
                <div className={styles.emptyState}>
                  <div className={styles.emptyStateContent}>
                    <div className={styles.emptyStateIcon}>
                      <Calendar />
                    </div>
                    <div>
                      <p className={styles.emptyStateTitle}>등록된 공휴일이 없습니다</p>
                      <p className={styles.emptyStateDescription}>
                        상단의 '공휴일 추가' 또는 '동기화' 버튼을 사용해 주세요
                      </p>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          ) : (
            sortedHolidays.map((holiday) => {
              const dayIndex = getDayOfWeekIndex(holiday.date);
              const dayClass = getDayClass(dayIndex);
              const isProtected = isNational(holiday);

              return (
                <tr
                  key={holiday.id}
                  className={isProtected ? styles.tableRowProtected : styles.tableRow}
                >
                  {/* 날짜 */}
                  <td className={styles.tableCellCenter}>
                    <span className={styles.monoText}>{holiday.date}</span>
                  </td>

                  {/* 요일 */}
                  <td className={`${styles.tableCellCenter} ${dayClass}`}>
                    {getDayOfWeekLabel(holiday.date)}
                  </td>

                  {/* 공휴일명 */}
                  <td className={styles.tableCellCenter}>
                    <div className={styles.holidayNameCellCenter}>
                      {isProtected && (
                        <span title="법정공휴일은 이름만 수정 가능합니다">
                          <Lock className={styles.lockIcon} />
                        </span>
                      )}
                      <span className={styles.holidayName}>{holiday.name}</span>
                    </div>
                  </td>

                  {/* 유형 */}
                  <td className={styles.tableCellCenter}>
                    <span className={getTypeBadgeClass(holiday.type)}>
                      {HOLIDAY_TYPE_LABELS[holiday.type]}
                    </span>
                  </td>

                  {/* 소스 */}
                  <td className={`${styles.tableCellCenter} ${styles.sourceText}`}>
                    {HOLIDAY_SOURCE_LABELS[holiday.source] || holiday.source}
                  </td>

                  {/* 수정일 — 정책: holiday.companyCode 기준 시차 */}
                  <td className={`${styles.tableCellCenter} ${styles.dateText}`}>
                    {formatDateTimeByCompanyCode(holiday.updatedAt || holiday.createdAt, holiday.companyCode)}
                  </td>

                  {/* 작업 (V60: read-only 시 숨김) */}
                  {(onEdit || onDelete) && (
                    <td className={styles.tableCellCenter}>
                      <div className={styles.actionCell}>
                        {onEdit && (
                          <button
                            onClick={() => onEdit(holiday)}
                            className={styles.editButton}
                            title={isProtected ? '이름만 수정 가능' : '수정'}
                          >
                            <Edit2 />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(holiday)}
                            className={isProtected ? styles.deleteButtonProtected : styles.deleteButton}
                            title={isProtected ? '법정공휴일은 보관(Archive)으로 삭제' : '삭제'}
                          >
                            <Trash2 />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* 테이블 푸터: 카운트 */}
      {!isLoading && sortedHolidays.length > 0 && (
        <div className={styles.tableFooter}>
          <span>총 <strong>{sortedHolidays.length}</strong>개의 공휴일</span>
          {(onEdit || onDelete) && <span className={styles.tableFooterHint}>클릭하여 수정, 휴지통 아이콘으로 삭제</span>}
        </div>
      )}
    </div>
  );
}
